<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900">
    <!-- Health Indicator -->
    <div
      :class="[
        'fixed top-5 right-5 z-50 px-4 py-2 rounded-full text-xs font-semibold flex items-center gap-2 shadow-md border transition-all duration-200',
        healthStatus === 'healthy'
          ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 border-green-500'
          : healthStatus === 'unhealthy'
            ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 border-red-500'
            : 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 border-yellow-500'
      ]"
    >
      <span
        :class="[
          'w-2 h-2 rounded-full',
          healthStatus === 'healthy'
            ? 'bg-green-500'
            : healthStatus === 'unhealthy'
              ? 'bg-red-500'
              : 'bg-yellow-500 animate-pulse'
        ]"
      ></span>
      <span>{{
        healthStatus === 'healthy'
          ? 'Healthy'
          : healthStatus === 'unhealthy'
            ? 'Unhealthy'
            : 'Checking...'
      }}</span>
    </div>

    <!-- Header -->
    <div
      :class="[
        theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
        'border-b px-6 py-4'
      ]"
    >
      <div class="flex items-center justify-between">
        <div>
          <h1
            :class="[theme.isDark.value ? 'text-gray-100' : 'text-gray-900', 'text-2xl font-bold']"
          >
            Team: {{ team?.name || 'Details' }}
          </h1>
        </div>
        <div class="flex items-center space-x-3">
          <button
            :class="[
              'px-4 py-2 rounded-md font-semibold shadow-sm transition-all',
              isEditMode
                ? 'bg-gray-500 hover:bg-gray-600 text-white'
                : 'bg-green-500 hover:bg-green-600 text-white'
            ]"
            @click="toggleEditMode"
          >
            {{ isEditMode ? 'Cancel Edit' : 'Edit' }}
          </button>
          <router-link
            to="/task-sentinel"
            class="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-md font-semibold shadow-sm transition-all"
          >
            ← Back to Teams
          </router-link>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="p-6 space-y-6">
      <!-- Error Message -->
      <div
        v-if="error"
        :class="[
          theme.isDark.value ? 'bg-red-900 border-red-700' : 'bg-red-50 border-red-200',
          'border rounded-md p-4'
        ]"
      >
        <div class="flex">
          <div class="flex-shrink-0">
            <svg
              class="h-5 w-5 text-red-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-red-800 dark:text-red-200">Error</h3>
            <div class="mt-2 text-sm text-red-700 dark:text-red-300">{{ error }}</div>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div
        v-if="loading"
        :class="[
          theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
          'border rounded-lg p-8'
        ]"
      >
        <div class="flex items-center justify-center">
          <svg
            class="animate-spin h-8 w-8 text-blue-500"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            />
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
          <span class="ml-3 text-lg text-gray-600 dark:text-gray-300">Loading team details...</span>
        </div>
      </div>

      <!-- Team Details -->
      <template v-else-if="team">
        <!-- View Mode -->
        <template v-if="!isEditMode">
          <!-- Basic Information -->
          <div
            :class="[
              theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
              'border rounded-lg p-6'
            ]"
          >
            <h2
              class="text-lg font-bold text-blue-500 mb-4 pb-3 border-b border-gray-200 dark:border-gray-700"
            >
              Basic Information
            </h2>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div class="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div
                  class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1"
                >
                  Team Name
                </div>
                <div class="text-gray-900 dark:text-white font-medium">{{ team.name || '-' }}</div>
              </div>
              <div class="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div
                  class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1"
                >
                  Team ID
                </div>
                <div class="text-gray-900 dark:text-white font-mono text-sm break-all">
                  {{ team.id || '-' }}
                </div>
              </div>
              <div class="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div
                  class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1"
                >
                  Folder ID
                </div>
                <div class="text-gray-900 dark:text-white font-mono text-sm">
                  <a
                    v-if="team.standupId"
                    :href="`https://app.clickup.com/8631005/v/f/${team.standupId}`"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-blue-600 dark:text-blue-400 hover:underline break-all"
                  >
                    {{ team.standupId }}
                  </a>
                  <span v-else>-</span>
                </div>
              </div>
              <div class="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div
                  class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1"
                >
                  Roadmap Board ID
                </div>
                <div class="text-gray-900 dark:text-white font-mono text-sm">
                  <template v-if="roadmapLinks.length > 0">
                    <template
                      v-for="(roadmapLink, index) in roadmapLinks"
                      :key="`${roadmapLink.id}-${index}`"
                    >
                      <a
                        :href="roadmapLink.href"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="text-blue-600 dark:text-blue-400 hover:underline break-all"
                      >
                        {{ roadmapLink.id }}
                      </a>
                      <span v-if="index < roadmapLinks.length - 1">, </span>
                    </template>
                  </template>
                  <span v-else>-</span>
                </div>
              </div>
              <div class="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div
                  class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1"
                >
                  Parent Group
                </div>
                <div class="text-gray-900 dark:text-white font-medium">
                  {{ team.parentGroup || '-' }}
                </div>
              </div>
              <div class="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div
                  class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1"
                >
                  Type
                </div>
                <div class="text-gray-900 dark:text-white font-medium">{{ team.type || '-' }}</div>
              </div>
              <div class="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div
                  class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1"
                >
                  Status
                </div>
                <div class="flex items-center gap-2">
                  <span
                    :class="[
                      'inline-block px-2.5 py-1 rounded text-xs font-semibold uppercase border',
                      team.deleted
                        ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 border-red-300 dark:border-red-700'
                        : 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 border-green-300 dark:border-green-700'
                    ]"
                  >
                    {{ team.deleted ? 'Deleted' : 'Active' }}
                  </span>
                  <span
                    v-if="team.isNew"
                    class="inline-block px-2.5 py-1 rounded text-xs font-semibold uppercase bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 border border-blue-300 dark:border-blue-700"
                  >
                    New
                  </span>
                </div>
              </div>
              <div class="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div
                  class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1"
                >
                  Send to EM Only
                </div>
                <div class="text-gray-900 dark:text-white font-medium">
                  {{ team.sendToEmOnly ? 'Yes' : 'No' }}
                </div>
              </div>
            </div>
          </div>

          <!-- Meta Information -->
          <div
            v-if="team.meta"
            :class="[
              theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
              'border rounded-lg p-6'
            ]"
          >
            <h2
              class="text-lg font-bold text-blue-500 mb-4 pb-3 border-b border-gray-200 dark:border-gray-700"
            >
              Meta Information
            </h2>
            <div class="space-y-3">
              <div class="flex items-center gap-2">
                <span class="text-gray-600 dark:text-gray-400 font-medium">Combined Folder:</span>
                <span class="text-gray-900 dark:text-white">{{
                  team.meta.isCombinedFolder ? 'Yes' : 'No'
                }}</span>
              </div>
              <div class="flex items-center gap-2">
                <span class="text-gray-600 dark:text-gray-400 font-medium"
                  >Skip Previous Quarter:</span
                >
                <span class="text-gray-900 dark:text-white">{{
                  team.meta.skipPreviousQuarter ? 'Yes' : 'No'
                }}</span>
              </div>
              <div
                v-if="team.meta.subModules && team.meta.subModules.length > 0"
                class="flex items-start gap-2"
              >
                <span class="text-gray-600 dark:text-gray-400 font-medium">Sub Modules:</span>
                <div class="flex flex-wrap gap-2">
                  <span
                    v-for="module in team.meta.subModules"
                    :key="module"
                    class="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-full text-sm font-medium border border-blue-300 dark:border-blue-700"
                  >
                    {{ module }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Managers -->
          <div
            :class="[
              theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
              'border rounded-lg p-6'
            ]"
          >
            <h2
              class="text-lg font-bold text-blue-500 mb-4 pb-3 border-b border-gray-200 dark:border-gray-700"
            >
              Managers
            </h2>
            <div
              v-if="sortedManagers.length > 0"
              class="space-y-2"
            >
              <div
                v-for="manager in sortedManagers"
                :key="`${manager.type}-${manager.emailPrefix}`"
                class="flex items-center gap-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
              >
                <span class="font-semibold text-blue-500 min-w-[100px]">{{ manager.type }}</span>
                <span class="text-gray-600 dark:text-gray-300 flex-1">{{
                  manager.emailPrefix
                }}</span>
                <span class="text-gray-500 dark:text-gray-400 font-mono text-sm">{{
                  manager.slackId || '-'
                }}</span>
              </div>
            </div>
            <p
              v-else
              class="text-gray-500 dark:text-gray-400"
            >
              No managers assigned
            </p>
          </div>

          <!-- Developers -->
          <div
            :class="[
              theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
              'border rounded-lg p-6'
            ]"
          >
            <h2
              class="text-lg font-bold text-blue-500 mb-4 pb-3 border-b border-gray-200 dark:border-gray-700"
            >
              Developers
            </h2>
            <div
              v-if="team.devs && team.devs.length > 0"
              class="space-y-2"
            >
              <div
                v-for="dev in team.devs"
                :key="dev.emailPrefix"
                class="flex items-center gap-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
              >
                <span class="font-semibold text-blue-500 min-w-[150px]">{{
                  dev.name || dev.emailPrefix
                }}</span>
                <span class="text-gray-600 dark:text-gray-300 flex-1">{{ dev.emailPrefix }}</span>
                <span class="text-gray-500 dark:text-gray-400 font-mono text-sm">{{
                  dev.slackId || '-'
                }}</span>
              </div>
            </div>
            <p
              v-else
              class="text-gray-500 dark:text-gray-400"
            >
              No developers assigned
            </p>
          </div>

          <!-- Additional Information -->
          <div
            :class="[
              theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
              'border rounded-lg p-6'
            ]"
          >
            <h2
              class="text-lg font-bold text-blue-500 mb-4 pb-3 border-b border-gray-200 dark:border-gray-700"
            >
              Additional Information
            </h2>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div class="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div
                  class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1"
                >
                  Date Added
                </div>
                <div class="text-gray-900 dark:text-white">{{ formatDate(team.dateAdded) }}</div>
              </div>
              <div class="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div
                  class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1"
                >
                  Date Updated
                </div>
                <div class="text-gray-900 dark:text-white">{{ formatDate(team.dateUpdated) }}</div>
              </div>
              <div class="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div
                  class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1"
                >
                  Ignore Devs
                </div>
                <div class="text-gray-900 dark:text-white">
                  {{
                    team.ignoreDevs && team.ignoreDevs.length > 0
                      ? team.ignoreDevs.join(', ')
                      : 'None'
                  }}
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- Edit Mode -->
        <template v-else>
          <TeamForm
            v-model:team="editableTeam"
            @lookup-email="lookupEmail"
          />

          <!-- Action Buttons -->
          <div
            :class="[
              theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
              'border rounded-lg p-6 flex gap-4'
            ]"
          >
            <button
              class="px-6 py-3 bg-yellow-500 hover:bg-yellow-600 text-white rounded-md font-semibold shadow-sm transition-all"
              @click="validateChanges"
            >
              Validate Changes
            </button>
            <button
              class="px-6 py-3 bg-green-500 hover:bg-green-600 text-white rounded-md font-semibold shadow-sm transition-all"
              @click="updateTeam"
            >
              Update Team
            </button>
            <button
              class="px-6 py-3 bg-gray-500 hover:bg-gray-600 text-white rounded-md font-semibold shadow-sm transition-all"
              @click="cancelEdit"
            >
              Cancel
            </button>
          </div>
        </template>
      </template>
    </div>

    <!-- Diff Modal -->
    <DiffModal
      v-if="showDiffModal"
      :old-data="diffData.current"
      :new-data="diffData.proposed"
      @close="showDiffModal = false"
    />

    <!-- Toast Notification -->
    <div
      v-if="toast.show"
      :class="[
        'fixed top-20 right-5 z-50 px-5 py-3 rounded-lg shadow-lg border font-medium max-w-md flex items-center gap-3 animate-slide-in',
        toast.type === 'success'
          ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 border-green-500'
          : toast.type === 'error'
            ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 border-red-500'
            : 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 border-blue-500'
      ]"
    >
      {{ toast.message }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, onUnmounted, inject, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useTheme } from '../../utils/useTheme'
import TeamForm from './components/TeamForm.vue'
import DiffModal from './components/DiffModal.vue'
import { taskSentinelService } from '../../services/task-sentinel'
import type { Team, ToastType, ValidationDiffData } from '../../types/task-sentinel'
import { logger } from '../../utils/logger'

const route = useRoute()
const theme = inject('theme', useTheme())

// State
const team = ref<Team | null>(null)
const editableTeam = ref<Team | null>(null)
const loading = ref(false)
const error = ref<string | null>(null)
const isEditMode = ref(false)
const healthStatus = ref<'healthy' | 'unhealthy' | 'checking'>('checking')
const showDiffModal = ref(false)
const diffData = reactive<ValidationDiffData>({
  current: null,
  proposed: null
})

// Toast notification
const toast = reactive({
  show: false,
  message: '',
  type: 'info' as ToastType
})

// Health check interval
let healthCheckInterval: ReturnType<typeof setInterval> | null = null

// Computed
const sortedManagers = computed(() => {
  if (!team.value?.managers) return []
  return taskSentinelService.sortManagersByType(team.value.managers)
})

const DEFAULT_ROADMAP_BASE_URL = 'https://app.clickup.com/8631005/v/li'

const parseRoadmapLinks = (value?: string) => {
  if (!value?.trim()) return []

  const tokens = value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)

  let baseUrl = DEFAULT_ROADMAP_BASE_URL
  const links: Array<{ id: string; href: string }> = []

  for (const token of tokens) {
    if (/^https?:\/\//i.test(token)) {
      try {
        const parsedUrl = new URL(token)
        const pathMatch = parsedUrl.pathname.match(/^(.*\/v\/li)\/([^/?#]+)/)

        if (pathMatch) {
          baseUrl = `${parsedUrl.origin}${pathMatch[1]}`
          const boardId = pathMatch[2]
          links.push({ id: boardId, href: `${baseUrl}/${boardId}` })
          continue
        }

        const fallbackId = parsedUrl.pathname.split('/').filter(Boolean).pop()
        if (fallbackId) {
          links.push({ id: fallbackId, href: token })
          continue
        }
      } catch {
        // Fall back to treating token as a board ID.
      }
    }

    const boardId = token.replace(/^\/+|\/+$/g, '')
    if (!boardId) continue
    links.push({ id: boardId, href: `${baseUrl}/${boardId}` })
  }

  return links
}

const roadmapLinks = computed(() => parseRoadmapLinks(team.value?.roadmapBoardId))

// Methods
const showToast = (message: string, type: ToastType = 'info') => {
  toast.message = message
  toast.type = type
  toast.show = true
  setTimeout(() => {
    toast.show = false
  }, 3000)
}

const loadTeam = async () => {
  const teamId = route.params.id as string
  if (!teamId) {
    error.value = 'No team ID provided'
    return
  }

  try {
    loading.value = true
    error.value = null

    const data = await taskSentinelService.fetchTeamById(teamId)
    team.value = data
    editableTeam.value = JSON.parse(JSON.stringify(data))

    logger.log('Team loaded:', { id: data.id, name: data.name })
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to load team'
    logger.error('Error loading team:', err)
  } finally {
    loading.value = false
  }
}

const toggleEditMode = () => {
  if (isEditMode.value) {
    // Cancel edit - restore original data
    editableTeam.value = JSON.parse(JSON.stringify(team.value))
  }
  isEditMode.value = !isEditMode.value
}

const cancelEdit = () => {
  editableTeam.value = JSON.parse(JSON.stringify(team.value))
  isEditMode.value = false
}

const validateChanges = async () => {
  if (!editableTeam.value || !team.value) {
    showToast('No team data to validate', 'error')
    return
  }

  try {
    // Try API validation first
    const payload = buildPayload()
    const response = await taskSentinelService.validateTeam(team.value.id, payload)

    if (response?.data) {
      diffData.current = response.data.current
      diffData.proposed = response.data.proposed
      showDiffModal.value = true
    } else {
      // Fallback to local diff if API doesn't return expected format
      showLocalDiff()
    }
  } catch (err) {
    // If API fails, show local diff instead
    logger.warn('API validation failed, showing local diff:', err)
    showLocalDiff()
  }
}

const showLocalDiff = () => {
  if (!editableTeam.value || !team.value) return

  // Create clean copies for comparison
  const currentData = JSON.parse(JSON.stringify(team.value))
  const proposedData = JSON.parse(JSON.stringify(editableTeam.value))

  // Remove fields that shouldn't be compared
  delete currentData.dateAdded
  delete currentData.dateUpdated
  delete proposedData.dateAdded
  delete proposedData.dateUpdated

  diffData.current = currentData
  diffData.proposed = proposedData
  showDiffModal.value = true

  showToast('Showing local diff comparison', 'info')
}

const updateTeam = async () => {
  if (!editableTeam.value || !team.value) return

  try {
    const payload = buildPayload()
    await taskSentinelService.updateTeam(team.value.id, payload)

    showToast('Team updated successfully!', 'success')

    // Reload and exit edit mode
    await loadTeam()
    isEditMode.value = false
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Update failed'
    showToast(message, 'error')
    logger.error('Update error:', err)
  }
}

const buildPayload = () => {
  if (!editableTeam.value) return {}

  return {
    name: editableTeam.value.name,
    standupId: editableTeam.value.standupId,
    roadmapBoardId: editableTeam.value.roadmapBoardId,
    parentGroup: editableTeam.value.parentGroup,
    type: editableTeam.value.type,
    deleted: editableTeam.value.deleted,
    isNew: editableTeam.value.isNew,
    sendToEmOnly: editableTeam.value.sendToEmOnly,
    managers: editableTeam.value.managers,
    devs: editableTeam.value.devs,
    ignoreDevs: editableTeam.value.ignoreDevs,
    meta: editableTeam.value.meta
  }
}

const lookupEmail = async (email: string, type: 'manager' | 'dev', index: number) => {
  try {
    const response = await taskSentinelService.lookupUserByEmail(email)
    if (response.success && response.data && editableTeam.value) {
      const emailPrefix = taskSentinelService.extractEmailPrefix(response.data.email)

      if (type === 'manager') {
        editableTeam.value.managers[index].emailPrefix = emailPrefix
        editableTeam.value.managers[index].slackId = response.data.slackId || ''
      } else {
        editableTeam.value.devs[index].emailPrefix = emailPrefix
        editableTeam.value.devs[index].slackId = response.data.slackId || ''
        editableTeam.value.devs[index].name = response.data.name || ''
      }

      showToast('User found and applied!', 'success')
    }
  } catch (err) {
    logger.error('Email lookup error:', err)
  }
}

const formatDate = (dateString: string | undefined): string => {
  return taskSentinelService.formatDate(dateString)
}

const checkHealth = async () => {
  healthStatus.value = 'checking'
  const isHealthy = await taskSentinelService.checkHealth()
  healthStatus.value = isHealthy ? 'healthy' : 'unhealthy'
}

// Lifecycle
onMounted(() => {
  loadTeam()
  checkHealth()
  healthCheckInterval = setInterval(checkHealth, 15000)
})

onUnmounted(() => {
  if (healthCheckInterval) {
    clearInterval(healthCheckInterval)
  }
})

// Watch for route changes
watch(
  () => route.params.id,
  () => {
    loadTeam()
  }
)
</script>

<style scoped>
@keyframes slide-in {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.animate-slide-in {
  animation: slide-in 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}
</style>
